import React from 'react';
import { useTranslation } from 'react-i18next';

import { GlobeIcon } from '../../components/icons';
import { useCustomizer } from '../../store/customizer';
import { MobileCta } from '../MobileCta';
import { Wrap, Header, Title, Subtitle, Stages, StageRow, Circle, StageLabel } from './MobileIntro.styles';

/** The four setup stages listed on the mobile intro (the first is the active
 *  starting point). `id` is a stable React key; `n` is the circle number; the
 *  final stage shows a globe. The visible label is translated at render time. */
const STAGES: ReadonlyArray<{ id: string; n?: string }> = [
  { id: 'region', n: '1' },
  { id: 'name', n: '2' },
  { id: 'logo', n: '3' },
  { id: 'golive' },
];

interface MobileIntroProps {
  /** Cross-dissolve the plate (set by `MobileApp` during the morph): forward it
   *  fades out to reveal step 1; on the way back it fades in to cover it. */
  dimmed?: boolean;
  /** Hide the real stage boxes while their flying ghost clones stand in, and lock
   *  the plate against input for the whole morph. */
  boxesHidden?: boolean;
}

/**
 * Mobile-only intro screen ("Take your website live") — the centered gradient
 * headline + subtitle and the four numbered setup stages, with the shared fixed
 * CTA that dismisses the intro and reveals step 1.
 *
 * During the dismiss/return morph (`MobileApp`) the plate cross-dissolves and the
 * real stage boxes hide the instant their flying ghost clones take over. Each box
 * carries `data-intro-stage` so the morph can measure its resting rect.
 */
export const MobileIntro = ({ dimmed = false, boxesHidden = false }: MobileIntroProps): React.ReactElement => {
  const { goNext } = useCustomizer();
  const { t } = useTranslation();

  const stageLabel: Record<string, string> = {
    region: t('Confirm Operating Region'),
    name: t('Choose Name & Web domain'),
    logo: t('Create Logo & Identity'),
    golive: t('Go live'),
  };

  return (
    <Wrap $dimmed={dimmed} $inTransition={boxesHidden}>
      <Header>
        <Title text="Take your website live" tag="h1" />
        <Subtitle text="Personalize your iGaming website and make it available to the world." tag="paragraph" />
      </Header>

      <Stages>
        {STAGES.map((s, i) => (
          <StageRow key={s.id}>
            <Circle $active={i === 0} $boxesHidden={boxesHidden} data-intro-stage={i}>
              {s.n ? s.n : <GlobeIcon size={18} />}
            </Circle>
            <StageLabel text={stageLabel[s.id]} tag="span" />
          </StageRow>
        ))}
      </Stages>

      <MobileCta label={t('Continue to Operating Region')} onClick={goNext} />
    </Wrap>
  );
};

export default MobileIntro;
